import React, { createContext, useContext, useMemo } from "react";
import { useJobPostCount, useCompaniesCount, useHcDomainList } from "@/hooks";

interface StatsContextProps {
  totalCompanies: number;
  totalJobPosts: number;
  totalHealthcareDomains: number;
  error: string | null;
  loading: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const StatsContext = createContext<StatsContextProps>({
  totalCompanies: 0,
  totalJobPosts: 0,
  totalHealthcareDomains: 0,
} as StatsContextProps);

interface StatsProviderProps {
  children: React.ReactNode;
}

export function StatsProvider({ children }: StatsProviderProps) {
  const {
    count: totalJobCount,
    error: jobsError,
    loading: jobsLoading,
  } = useJobPostCount();

  const {
    count: allCompanies,
    error: companiesError,
    loading: companiesLoading,
  } = useCompaniesCount();

  const {
    totalDomains: allDomains,
    error: domainsError,
    loading: domainsLoading,
  } = useHcDomainList();

  const totalCompanies = allCompanies ? allCompanies : 0;
  const totalJobPosts = totalJobCount ? totalJobCount : 0;
  const totalHealthcareDomains = allDomains ? allDomains : 0;

  const errors = jobsError || companiesError || domainsError;
  const currentlyLoading = jobsLoading || companiesLoading || domainsLoading;

  const value = useMemo(
    () => ({
      totalCompanies,
      totalJobPosts,
      totalHealthcareDomains,
      error: errors,
      loading: currentlyLoading,
    }),
    [
      totalCompanies,
      totalJobPosts,
      totalHealthcareDomains,
      errors,
      currentlyLoading,
    ],
  );

  return (
    <StatsContext.Provider value={value}>{children}</StatsContext.Provider>
  );
}

export function useStatsContext() {
  const context = useContext(StatsContext);

  if (!context) {
    throw new Error("useStatsContext must be used within a StatsProvider");
  }

  return context;
}
